import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EditorPage from "@/pages/EditorPage";
import PublicMenuPage from "@/pages/PublicMenuPage";
import MobileEditPage from "@/pages/MobileEditPage";
import AdminPage from "@/pages/AdminPage";
import SlideshowPublicPage from "@/pages/SlideshowPublicPage";
import CalendarPublicPage from "@/pages/CalendarPublicPage";
import LandingEditorPage from "@/pages/LandingEditorPage";
import SurveyPage from "@/pages/SurveyPage";
import ClientsPage from "@/pages/ClientsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import CouponsPage from "@/pages/CouponsPage";
import PublicInvoicePage from "@/pages/PublicInvoicePage";
import ClientRegisterPage from "@/pages/ClientRegisterPage";
import OrdersPage from "@/pages/OrdersPage";
import PublicTrackingPage from "@/pages/PublicTrackingPage";
import ProductsPage from "@/pages/ProductsPage";
import FinancialsPage from "@/pages/FinancialsPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-stone-600">Cargando...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Component to show landing page for non-authenticated users, dashboard for authenticated
const HomeRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="animate-pulse text-white">Cargando...</div>
      </div>
    );
  }
  
  if (user) {
    return <DashboardPage />;
  }
  
  return <LandingPage />;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/menu/:menuId" element={<PublicMenuPage />} />
            <Route path="/slideshow/:slideshowId" element={<SlideshowPublicPage />} />
            <Route path="/calendar/:calendarId" element={<CalendarPublicPage />} />
            <Route path="/encuesta" element={<SurveyPage />} />
            <Route path="/survey" element={<SurveyPage />} />
            <Route path="/invoice/:invoiceId" element={<PublicInvoicePage />} />
            <Route path="/register" element={<ClientRegisterPage />} />
            <Route path="/tracking/:trackingNumber" element={<PublicTrackingPage />} />
            <Route path="/tracking" element={<PublicTrackingPage />} />
            <Route path="/mobile" element={<MobileEditPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editor/:menuId"
              element={
                <ProtectedRoute>
                  <EditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/landing-editor"
              element={
                <ProtectedRoute>
                  <LandingEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/coupons"
              element={
                <ProtectedRoute>
                  <CouponsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute>
                  <ProductsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/financials"
              element={
                <ProtectedRoute>
                  <FinancialsPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
